import { useQuery } from "@apollo/client";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Grid, IconButton, Paper, Stack, Typography, useTheme } from "@mui/material";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { GET_SERC_AGENCY_BY_STATE } from "routes/Staff/Agencies/Sercs/schema";

import { FireDepartmentDisplay, LepcDisplay, SercDisplay } from "./AgencyDisplay";
import { useFulfillmentDetails } from "./useFulfillmentDetails";

export function Agencies() {
  const theme = useTheme();
  const { data } = useFulfillmentDetails();

  const facilityState = data?.tierIIReport.facility.state;
  const lepc = data?.tierIIReport.facility.lepc;
  const fireDepartment = data?.tierIIReport.facility.fireDepartment;

  const { data: sercData, loading: sercLoading } = useQuery(GET_SERC_AGENCY_BY_STATE, {
    variables: { state: facilityState ?? "" },
    skip: !facilityState,
  });
  const serc = sercData?.sercAgencyByState ?? null;

  // Determine if the associated facility is in California
  const isCA = facilityState === "CA";

  // Dynamic label for LEPC/CUPA
  const lepcLabel = isCA ? "CUPA" : "LEPC";

  // State for controlling main collapse
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "fulfillment-agencies-expanded",
    { defaultValue: true }
  );

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      spacing={2}
      padding={theme.spacing(2, 3, 2, 3)}
      sx={{ width: "100%" }}
    >
      {/* Main Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="h6">Local Agencies</Typography>
        <IconButton size="small">{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>

      {/* All fields in a single Grid container for consistent spacing */}
      <Grid container spacing={3}>
        {/* First column: LEPC info */}
        <LepcDisplay lepc={lepc} lepcLabel={lepcLabel} isExpanded={isExpanded} />

        {/* Second column: Fire Department info */}
        <FireDepartmentDisplay fireDepartment={fireDepartment} isExpanded={isExpanded} />

        {/* Third column: SERC (fetched by facility state) */}
        <SercDisplay
          serc={serc}
          loading={facilityState ? sercLoading : false}
          isExpanded={isExpanded}
        />
      </Grid>
    </Stack>
  );
}
